// ═══════════════════════════════════════════════════════════════
// BLACKBOARD — Dashboard
// Unified stats, compliance chart, recent activity, expiration tracking
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Text, SimpleGrid, Table, Badge, Loader,
  Center, Title, Modal, TextInput,
} from '../../components/gab/GabComponents';
import {
  IconUsers, IconBook, IconChartPie, IconAlertTriangle, IconSearch,
} from '@tabler/icons-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import dayjs from 'dayjs';
import { useBlackboardStore } from '../../store/blackboardStore';
import type { BBUser, TrainingRecord } from '../../store/blackboardStore';

// ── Helpers ────────────────────────────────────────────────────

function userName(user: BBUser): string {
  const full = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return full || user.email || 'Unknown';
}

function isRecordCompliant(r: TrainingRecord): boolean {
  if (!r.completedDate) return false;
  if (!r.expiresDate) return true;
  return dayjs(r.expiresDate).isAfter(dayjs());
}

function expiryBadge(daysRemaining: number) {
  if (daysRemaining < 0) return <Badge color="red">Overdue</Badge>;
  if (daysRemaining <= 7) return <Badge color="red">{daysRemaining}d left</Badge>;
  if (daysRemaining <= 14) return <Badge color="orange">{daysRemaining}d left</Badge>;
  if (daysRemaining <= 30) return <Badge color="yellow">{daysRemaining}d left</Badge>;
  return <Badge color="green">Valid</Badge>;
}

function complianceBadge(pct: number) {
  if (pct >= 90) return <Badge color="green">{pct}%</Badge>;
  if (pct >= 70) return <Badge color="yellow">{pct}%</Badge>;
  if (pct >= 50) return <Badge color="orange">{pct}%</Badge>;
  return <Badge color="red">{pct}%</Badge>;
}

function trainingStatusBadge(status: string) {
  const map: Record<string, { color: string; label: string }> = {
    completed: { color: 'green', label: 'Completed' },
    compliant: { color: 'green', label: 'Compliant' },
    'in-progress': { color: 'blue', label: 'In Progress' },
    expired: { color: 'red', label: 'Expired' },
    overdue: { color: 'red', label: 'Overdue' },
    pending: { color: 'yellow', label: 'Pending' },
  };
  const cfg = map[status] || { color: 'gray', label: status || 'Unknown' };
  return <Badge color={cfg.color}>{cfg.label}</Badge>;
}

// ── Stat Card ──────────────────────────────────────────────────

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  onClick?: () => void;
}

function StatCard({ title, value, icon, color, onClick }: StatCardProps) {
  return (
    <Paper
      p="sm"
      withBorder
      shadow="sm"
      radius="md"
      ta="center"
      style={{
        borderTop: `3px solid ${color}`,
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
    >
      <Box style={{ color, display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
        {icon}
      </Box>
      <Text fz={28} fw={700}>{value}</Text>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{title}</Text>
    </Paper>
  );
}

// ── Section Header ─────────────────────────────────────────────

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <Box mt="xl" mb="sm">
      <Text
        size="10px"
        fw={700}
        c="dimmed"
        mb="xs"
        pb={6}
        style={{ textTransform: 'uppercase', letterSpacing: 2, borderBottom: '1px solid #dee2e6' }}
      >
        {children}
      </Text>
    </Box>
  );
}

// ── Detail Modal ───────────────────────────────────────────────

function DetailModal({
  opened, onClose, title, children,
}: {
  opened: boolean; onClose: () => void; title: string; children: React.ReactNode;
}) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={700} size="lg">{title}</Text>}
      size="xl"
      radius="md"
      centered
    >
      {children}
    </Modal>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────

export default function Dashboard() {
  const {
    users, trainings, trainingRecords, loading,
    loadUsers, loadTrainings, loadTrainingRecords, loadClassrooms,
  } = useBlackboardStore();

  const [personnelModal, setPersonnelModal] = useState(false);
  const [trainingsModal, setTrainingsModal] = useState(false);
  const [complianceModal, setComplianceModal] = useState(false);
  const [expiringModal, setExpiringModal] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');

  useEffect(() => {
    loadUsers();
    loadTrainings();
    loadTrainingRecords();
    loadClassrooms();
  }, [loadUsers, loadTrainings, loadTrainingRecords, loadClassrooms]);

  const now = dayjs();

  // ── Stats ────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const totalPersonnel = users.length;
    const activeTrainings = trainings.filter(t => t.active !== 'false').length;

    const totalRecords = trainingRecords.length;
    const compliantRecords = trainingRecords.filter(isRecordCompliant).length;
    const complianceRate = totalRecords > 0
      ? Math.round((compliantRecords / totalRecords) * 100)
      : 100;

    const expiringSoon = trainingRecords.filter(r => {
      if (!r.expiresDate) return false;
      const exp = dayjs(r.expiresDate);
      return exp.isAfter(now) && exp.diff(now, 'day') <= 30;
    }).length;

    return { totalPersonnel, activeTrainings, complianceRate, expiringSoon };
  }, [users, trainings, trainingRecords, now]);

  // ── Expiring records ─────────────────────────────────────────
  const expiringRecords = useMemo(() => {
    return trainingRecords
      .filter(r => {
        if (!r.expiresDate) return false;
        const exp = dayjs(r.expiresDate);
        return exp.isAfter(now) && exp.diff(now, 'day') <= 30;
      })
      .map(r => {
        const exp = dayjs(r.expiresDate);
        const user = users.find(u => String(u.id) === r.userId);
        const training = trainings.find(t => String(t.id) === r.trainingId);
        return {
          id: r.id,
          userId: r.userId,
          userName: user ? userName(user) : (r.userName || 'Unknown'),
          trainingName: training?.name || r.trainingName || 'Unknown',
          expiresAt: exp,
          daysRemaining: exp.diff(now, 'day'),
        };
      })
      .sort((a, b) => a.expiresAt.unix() - b.expiresAt.unix());
  }, [trainingRecords, users, trainings, now]);

  // ── Upcoming expirations (top 10) ────────────────────────────
  const upcomingExpirations = useMemo(() => {
    return trainingRecords
      .filter(r => r.expiresDate)
      .map(r => {
        const exp = dayjs(r.expiresDate);
        const user = users.find(u => String(u.id) === r.userId);
        const training = trainings.find(t => String(t.id) === r.trainingId);
        return {
          id: r.id,
          userId: r.userId,
          userName: user ? userName(user) : (r.userName || 'Unknown'),
          trainingName: training?.name || r.trainingName || 'Unknown',
          expiresAt: exp,
          daysRemaining: exp.diff(now, 'day'),
        };
      })
      .sort((a, b) => a.expiresAt.unix() - b.expiresAt.unix())
      .slice(0, 10);
  }, [trainingRecords, users, trainings, now]);

  // ── Compliance by category ───────────────────────────────────
  const complianceByCategory = useMemo(() => {
    const catMap: Record<string, { total: number; compliant: number }> = {};

    trainings.forEach(t => {
      const cat = t.category || 'Uncategorized';
      if (!catMap[cat]) catMap[cat] = { total: 0, compliant: 0 };
    });

    trainingRecords.forEach(r => {
      const training = trainings.find(t => String(t.id) === r.trainingId);
      const cat = training?.category || 'Uncategorized';
      if (!catMap[cat]) catMap[cat] = { total: 0, compliant: 0 };
      catMap[cat].total += 1;
      if (isRecordCompliant(r)) catMap[cat].compliant += 1;
    });

    return Object.entries(catMap).map(([name, { total, compliant }]) => ({
      name,
      compliance: total > 0 ? Math.round((compliant / total) * 100) : 100,
    }));
  }, [trainings, trainingRecords]);

  // ── Per-user compliance ──────────────────────────────────────
  const perUserCompliance = useMemo(() => {
    return users.map(u => {
      const userRecords = trainingRecords.filter(r => r.userId === String(u.id) || r.userName === `${u.firstName} ${u.lastName}`.trim());
      const total = userRecords.length;
      const compliant = userRecords.filter(isRecordCompliant).length;
      const pct = total > 0 ? Math.round((compliant / total) * 100) : 100;
      return { id: u.id, name: userName(u), email: u.email, role: u.role, compliant, total, pct };
    });
  }, [users, trainingRecords]);

  // ── Recent activity ──────────────────────────────────────────
  const recentActivity = useMemo(() => {
    return trainingRecords
      .filter(r => r.completedDate)
      .map(r => {
        const user = users.find(u => String(u.id) === r.userId);
        const training = trainings.find(t => String(t.id) === r.trainingId);
        return {
          id: r.id,
          userId: r.userId,
          userName: user ? userName(user) : (r.userName || 'Unknown'),
          trainingName: training?.name || r.trainingName || 'Unknown',
          date: r.completedDate || r.datecreated,
          status: r.status || 'completed',
        };
      })
      .sort((a, b) => {
        const da = a.date ? new Date(a.date).getTime() : 0;
        const db2 = b.date ? new Date(b.date).getTime() : 0;
        return db2 - da;
      })
      .slice(0, 10);
  }, [trainingRecords, users, trainings]);

  // ── Loading ──────────────────────────────────────────────────
  if (loading && users.length === 0) {
    return (
      <Center h={300}>
        <Loader size="lg" color="blue" />
      </Center>
    );
  }

  const today = dayjs().format('dddd, MMMM D, YYYY');

  return (
    <Box>
      {/* Welcome Header */}
      <Box mb="lg">
        <Title order={3} fw={700} c="black">Training Dashboard</Title>
        <Text size="sm" c="dimmed" mt={2}>{today}</Text>
      </Box>

      {/* Stat Cards */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
        <StatCard
          title="Total Personnel"
          value={stats.totalPersonnel}
          icon={<IconUsers size={16} />}
          color="#4a82c4"
          onClick={() => { setSearchFilter(''); setPersonnelModal(true); }}
        />
        <StatCard
          title="Active Trainings"
          value={stats.activeTrainings}
          icon={<IconBook size={16} />}
          color="#334e68"
          onClick={() => { setSearchFilter(''); setTrainingsModal(true); }}
        />
        <StatCard
          title="Compliance Rate"
          value={`${stats.complianceRate}%`}
          icon={<IconChartPie size={16} />}
          color="#16a34a"
          onClick={() => { setSearchFilter(''); setComplianceModal(true); }}
        />
        <StatCard
          title="Expiring Soon"
          value={stats.expiringSoon}
          icon={<IconAlertTriangle size={16} />}
          color="#dc2626"
          onClick={() => { setSearchFilter(''); setExpiringModal(true); }}
        />
      </SimpleGrid>

      {/* ── Modals ──────────────────────────────────────────────── */}

      {/* Personnel Modal */}
      <DetailModal opened={personnelModal} onClose={() => setPersonnelModal(false)} title="All Personnel">
        <TextInput
          placeholder="Search by name or email..."
          mb="md"
          leftSection={<IconSearch size={14} />}
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
        />
        <Table.ScrollContainer minWidth={500}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>Position</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {users
                .filter(u => {
                  if (!searchFilter) return true;
                  const s = searchFilter.toLowerCase();
                  return userName(u).toLowerCase().includes(s) || (u.email || '').toLowerCase().includes(s);
                })
                .map(u => (
                  <Table.Tr key={u.id}>
                    <Table.Td>
                      <Text size="sm" fw={500} c="#4a82c4">{userName(u)}</Text>
                    </Table.Td>
                    <Table.Td>{u.email || '---'}</Table.Td>
                    <Table.Td>
                      <Badge variant="light" color="blue" tt="capitalize">{u.role || 'student'}</Badge>
                    </Table.Td>
                    <Table.Td>{u.position || '---'}</Table.Td>
                  </Table.Tr>
                ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </DetailModal>

      {/* Trainings Modal */}
      <DetailModal opened={trainingsModal} onClose={() => setTrainingsModal(false)} title="All Training Definitions">
        <TextInput
          placeholder="Search trainings..."
          mb="md"
          leftSection={<IconSearch size={14} />}
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
        />
        <Table.ScrollContainer minWidth={500}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Category</Table.Th>
                <Table.Th>Frequency (days)</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {trainings
                .filter(t => {
                  if (!searchFilter) return true;
                  const s = searchFilter.toLowerCase();
                  return (t.name || '').toLowerCase().includes(s) || (t.category || '').toLowerCase().includes(s);
                })
                .map(t => (
                  <Table.Tr key={t.id}>
                    <Table.Td fw={500}>{t.name || '---'}</Table.Td>
                    <Table.Td>{t.category || '---'}</Table.Td>
                    <Table.Td>{t.frequencyDays > 0 ? `${t.frequencyDays} days` : 'One-time'}</Table.Td>
                  </Table.Tr>
                ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </DetailModal>

      {/* Compliance Modal */}
      <DetailModal opened={complianceModal} onClose={() => setComplianceModal(false)} title="Per-User Compliance Breakdown">
        <TextInput
          placeholder="Search by name..."
          mb="md"
          leftSection={<IconSearch size={14} />}
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
        />
        <Table.ScrollContainer minWidth={500}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Compliant</Table.Th>
                <Table.Th>Total Records</Table.Th>
                <Table.Th>Compliance</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {perUserCompliance
                .filter(u => {
                  if (!searchFilter) return true;
                  return (u.name || '').toLowerCase().includes(searchFilter.toLowerCase());
                })
                .sort((a, b) => a.pct - b.pct)
                .map(u => (
                  <Table.Tr key={u.id}>
                    <Table.Td>
                      <Text size="sm" fw={500} c="#4a82c4">{u.name}</Text>
                    </Table.Td>
                    <Table.Td>{u.compliant}</Table.Td>
                    <Table.Td>{u.total}</Table.Td>
                    <Table.Td>{u.pct}%</Table.Td>
                    <Table.Td>{complianceBadge(u.pct)}</Table.Td>
                  </Table.Tr>
                ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </DetailModal>

      {/* Expiring Soon Modal */}
      <DetailModal opened={expiringModal} onClose={() => setExpiringModal(false)} title="Trainings Expiring Within 30 Days">
        <TextInput
          placeholder="Search by name or training..."
          mb="md"
          leftSection={<IconSearch size={14} />}
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
        />
        <Table.ScrollContainer minWidth={500}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Student</Table.Th>
                <Table.Th>Training</Table.Th>
                <Table.Th>Expires</Table.Th>
                <Table.Th>Days Remaining</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {expiringRecords
                .filter(r => {
                  if (!searchFilter) return true;
                  const s = searchFilter.toLowerCase();
                  return (r.userName || '').toLowerCase().includes(s) || (r.trainingName || '').toLowerCase().includes(s);
                })
                .map(row => (
                  <Table.Tr key={row.id}>
                    <Table.Td>
                      <Text size="sm" fw={500} c="#4a82c4">{row.userName}</Text>
                    </Table.Td>
                    <Table.Td>{row.trainingName}</Table.Td>
                    <Table.Td>{row.expiresAt.format('MMM D, YYYY')}</Table.Td>
                    <Table.Td fw={600}>{row.daysRemaining}</Table.Td>
                    <Table.Td>{expiryBadge(row.daysRemaining)}</Table.Td>
                  </Table.Tr>
                ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </DetailModal>

      {/* ── Two-column: Expirations + Chart ─────────────────────── */}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md" mt="lg">
        {/* Upcoming Expirations */}
        <Paper p="md" withBorder shadow="sm" radius="md">
          <SectionHeader>Upcoming Expirations</SectionHeader>
          {upcomingExpirations.length === 0 ? (
            <Text c="dimmed" size="sm" ta="center" py="lg">No upcoming expirations</Text>
          ) : (
            <Table.ScrollContainer minWidth={400}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Training</Table.Th>
                    <Table.Th>Expires</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {upcomingExpirations.map(row => (
                    <Table.Tr key={row.id}>
                      <Table.Td>
                        <Text size="sm" fw={500} c="#4a82c4">{row.userName}</Text>
                      </Table.Td>
                      <Table.Td>{row.trainingName}</Table.Td>
                      <Table.Td>{row.expiresAt.format('MMM D, YYYY')}</Table.Td>
                      <Table.Td>{expiryBadge(row.daysRemaining)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Paper>

        {/* Compliance by Category Chart */}
        <Paper p="md" withBorder shadow="sm" radius="md">
          <SectionHeader>Compliance by Category</SectionHeader>
          {complianceByCategory.length === 0 ? (
            <Text c="dimmed" size="sm" ta="center" py="lg">No data available</Text>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={complianceByCategory} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                <Tooltip
                  formatter={(value: any) => [`${value}%`, 'Compliance']}
                  contentStyle={{ borderRadius: 8, fontSize: 12 }}
                />
                <Bar dataKey="compliance" fill="#4a82c4" radius={[4, 4, 0, 0]} maxBarSize={50} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </SimpleGrid>

      {/* ── Recent Activity ─────────────────────────────────────── */}
      <Paper p="md" withBorder shadow="sm" radius="md" mt="lg">
        <SectionHeader>Recent Activity</SectionHeader>
        {recentActivity.length === 0 ? (
          <Text c="dimmed" size="sm" ta="center" py="lg">No recent activity</Text>
        ) : (
          <Table.ScrollContainer minWidth={500}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Training</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {recentActivity.map(row => (
                  <Table.Tr key={row.id}>
                    <Table.Td>
                      <Text size="sm" fw={500} c="#4a82c4">{row.userName}</Text>
                    </Table.Td>
                    <Table.Td>{row.trainingName}</Table.Td>
                    <Table.Td>
                      {row.date ? dayjs(row.date).format('MMM D, YYYY') : '---'}
                    </Table.Td>
                    <Table.Td>{trainingStatusBadge(row.status)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Paper>
    </Box>
  );
}
